export interface ScreenPoint {
  x: number;
  y: number;
}

export interface WorldTarget {
  x: number;
  y: number;
  z: number;
}

export interface LogoFormation {
  points: ScreenPoint[];
  aspectRatio: number;
}

export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ScreenBox {
  width: number;
  height: number;
}

const LOGO_ALPHA_THRESHOLD = 16;

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function pixelHash(x: number, y: number): number {
  let h = Math.imul(x + 0x9e3779b9, 0x85ebca6b) ^ Math.imul(y + 0xc2b2ae35, 0x27d4eb2f);
  h ^= h >>> 16;
  return h >>> 0;
}

function seededRandom(seed: number): () => number {
  let state = seed || 0x6d2b79f5;
  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

export function assignLogoTargetsToWorldTargets<T extends WorldTarget>(
  logoPoints: readonly ScreenPoint[],
  worldTargets: readonly T[],
): T[] {
  const n = Math.min(logoPoints.length, worldTargets.length);
  const logoOrder = Array.from({ length: n }, (_, i) => i).sort((a, b) => {
    const ay = logoPoints[a]?.y ?? 0;
    const by = logoPoints[b]?.y ?? 0;
    if (ay !== by) return ay - by;
    return (logoPoints[a]?.x ?? 0) - (logoPoints[b]?.x ?? 0);
  });
  const worldOrder = Array.from({ length: n }, (_, i) => i).sort((a, b) => {
    const az = worldTargets[a]?.z ?? 0;
    const bz = worldTargets[b]?.z ?? 0;
    if (az !== bz) return bz - az;
    return (worldTargets[a]?.x ?? 0) - (worldTargets[b]?.x ?? 0);
  });

  const assigned = new Array<T>(n);
  for (let rank = 0; rank < n; rank++) {
    assigned[logoOrder[rank]!] = worldTargets[worldOrder[rank]!]!;
  }
  return assigned;
}

export function logoPointToScreenPoint(point: ScreenPoint, rect: ScreenRect): ScreenPoint {
  return {
    x: rect.left + point.x * rect.width,
    y: rect.top + point.y * rect.height,
  };
}

export function fitLogoRect(box: ScreenBox, aspectRatio: number): ScreenRect {
  const safeAspect = aspectRatio > 0 ? aspectRatio : 4;
  const boxAspect = box.width / Math.max(1, box.height);
  if (boxAspect > safeAspect) {
    const width = box.height * safeAspect;
    return {
      left: (box.width - width) * 0.5,
      top: 0,
      width,
      height: box.height,
    };
  }

  const height = box.width / safeAspect;
  return {
    left: 0,
    top: (box.height - height) * 0.5,
    width: box.width,
    height,
  };
}

export function sampleLogoMaskPoints(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  targetCount: number,
): LogoFormation {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = rgba[(y * width + x) * 4 + 3] ?? 0;
      if (alpha <= LOGO_ALPHA_THRESHOLD) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (maxX < minX || maxY < minY) {
    return {
      points: Array.from({ length: targetCount }, (_, i) => ({
        x: targetCount <= 1 ? 0.5 : i / (targetCount - 1),
        y: 0.5,
      })),
      aspectRatio: 4,
    };
  }

  const boxW = Math.max(1, maxX - minX + 1);
  const boxH = Math.max(1, maxY - minY + 1);
  const points: ScreenPoint[] = [];
  const used = new Set<number>();
  const maxAttempts = Math.max(4000, targetCount * 96);
  const random = seededRandom(pixelHash(width ^ targetCount, height));

  for (let i = 0; points.length < targetCount && i < maxAttempts; i++) {
    const x = minX + Math.min(boxW - 1, Math.floor(random() * boxW));
    const y = minY + Math.min(boxH - 1, Math.floor(random() * boxH));
    const alpha = rgba[(y * width + x) * 4 + 3] ?? 0;
    if (alpha <= LOGO_ALPHA_THRESHOLD) continue;
    const key = y * width + x;
    if (used.has(key)) continue;
    used.add(key);
    points.push({
      x: clamp01((x - minX + 0.5) / boxW),
      y: clamp01((y - minY + 0.5) / boxH),
    });
  }

  if (points.length < targetCount) {
    const candidates: Array<ScreenPoint & { h: number }> = [];
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const alpha = rgba[(y * width + x) * 4 + 3] ?? 0;
        if (alpha <= LOGO_ALPHA_THRESHOLD) continue;
        const key = y * width + x;
        if (used.has(key)) continue;
        candidates.push({
          x: clamp01((x - minX + 0.5) / boxW),
          y: clamp01((y - minY + 0.5) / boxH),
          h: pixelHash(x, y),
        });
      }
    }
    candidates.sort((a, b) => a.h - b.h);
    for (const candidate of candidates) {
      if (points.length >= targetCount) break;
      points.push({ x: candidate.x, y: candidate.y });
    }
  }

  if (points.length < targetCount) {
    const seeded = points.length > 0 ? points : [{ x: 0.5, y: 0.5 }];
    return {
      points: Array.from({ length: targetCount }, (_, i) => seeded[i % seeded.length]!),
      aspectRatio: boxW / boxH,
    };
  }

  return {
    points,
    aspectRatio: boxW / boxH,
  };
}

export async function loadLogoFormation(url: string, targetCount: number): Promise<LogoFormation> {
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  await img.decode();

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, img.naturalWidth || 1024);
  canvas.height = Math.max(1, img.naturalHeight || 320);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Logo canvas unavailable');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return sampleLogoMaskPoints(data.data, data.width, data.height, targetCount);
}
